import { ParallaxHeader } from '@components/parallaxHeader';
import { SimpleLink } from '@components/simpleLink';
import { GutmannLogo } from '@icons/GutmannLogo';
import { cn, round } from '@utils/helpers';
import LanguageMenuToggle from 'front/assets/other-icons/language-menu-toggle.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFragment } from 'react-relay';
import { Link as RouterLink } from 'react-router-dom';
import { pageHeaderFragment, pageHeaderSiteInfoFragment, pageHeaderTranslationsFragment } from './PageHeader.relay';
import { AuthButton } from './lib/AuthButton';
import { SearchPageField } from './lib/searchPageField';
import type { PageHeaderPropsType } from './types';

function PageHeader({ changeLanguageFn, currentLanguage, pageHeader, siteInfo, translations, needsNavigation, onSmallHeaderChange }: PageHeaderPropsType) {
  const pageHeaderData = useFragment(pageHeaderFragment, pageHeader);
  const siteInfoData = useFragment(pageHeaderSiteInfoFragment, siteInfo);
  const translationsData = useFragment(pageHeaderTranslationsFragment, translations as any) as Array<{ language: string; url: string }>;

  const [menuIsVisible, setMenuIsVisible] = useState(false);
  const [languageMenuIsVisible, setLanguageMenuIsVisible] = useState(false);
  const [languageSwitchVisible, setLanguageSwitchVisible] = useState(false);
  const [imageLuminosity, setImageLuminosity] = useState<number | null>(null);

  const languages = useMemo(() => translationsData.map((translation) => translation.language), [translationsData]);

  // Notify parent about smallHeader changes instead of DOM manipulation
  useEffect(() => {
    onSmallHeaderChange?.(pageHeaderData.smallHeader);
  }, [pageHeaderData.smallHeader, onSmallHeaderChange]);

  // Toggle no-bonncapps body class for non-latin font fallback
  useEffect(() => {
    const langCode = currentLanguage.split('-')[0];
    const needsFallback = ['ru', 'cs', 'cz'].includes(langCode);
    document.body.classList.toggle('no-bonncapps', needsFallback);
  }, [currentLanguage]);

  const toggleLanguageMenu = useCallback(
    (newValue: boolean | null = null) => {
      const setValue = newValue !== null ? newValue : !languageMenuIsVisible;
      setLanguageMenuIsVisible(setValue);
    },
    [languageMenuIsVisible],
  );

  const toggleMenu = useCallback(
    (newValue: boolean | null = null) => {
      const setValue = newValue !== null ? newValue : !menuIsVisible;
      setMenuIsVisible(setValue);
    },
    [menuIsVisible],
  );

  const toggleLanguageSwitch = useCallback(
    (newValue: boolean | null = null) => {
      const setValue = newValue !== null ? newValue : !languageSwitchVisible;
      setLanguageSwitchVisible(setValue);
    },
    [languageSwitchVisible],
  );

  const changeLanguage = useCallback(
    (key: string) => {
      setLanguageSwitchVisible(false);
      const url = translationsData.find((translation) => translation.language === key)?.url;
      if (url) {
        changeLanguageFn(key, url);
      }
    },
    [translationsData, changeLanguageFn],
  );

  const setImageData = useCallback((data: { r: number; g: number; b: number }) => {
    setImageLuminosity(round((data.r + data.g + data.b) / 3 / 255, 2));
  }, []);

  const { smallHeader } = pageHeaderData;
  const langCode = currentLanguage.split('-')[0];

  const textShadow = useMemo(() => {
    const rgba = `rgba(255, 255, 255, ${Math.max(1 - (imageLuminosity ?? 0), 0.4)})`;
    return `0 0 10px ${rgba}, 0 0 15px ${rgba}, 0 0 20px ${rgba}`;
  }, [imageLuminosity]);

  const handleLanguageNavBlur = useCallback(() => {
    setLanguageSwitchVisible(false);
  }, []);

  return (
    <div style={{ ['--header-text-shadow' as string]: textShadow }}>
      <ParallaxHeader pageImageUrl={pageHeaderData.pageImageUrl} pageBackgroundImageUrl={pageHeaderData.pageBackgroundImageUrl} forceSmall={smallHeader} setImageData={setImageData}>
        <button type="button" className="Header-Language-Menu-toggle" onClick={() => toggleLanguageMenu()}>
          <LanguageMenuToggle />
        </button>
        <nav className={cn('Header-Language', { 'is-visible': languageSwitchVisible }, { 'is-menu-visible': languageMenuIsVisible })} onBlur={handleLanguageNavBlur}>
          <button
            type="button"
            className="Header-Language-Menu-close Header-Navigation-close"
            onClick={() => {
              toggleLanguageMenu(false);
            }}
          >
            &times;
          </button>
          {/* Lead language abbreviation — hidden on mobile via CSS, shown on desktop when switch is collapsed */}
          {!languageSwitchVisible && (
            <button type="button" className="Header-Language-item Header-Language-item-lead active max-[800px]:hidden" onClick={() => toggleLanguageSwitch()}>
              {langCode.toUpperCase()}
            </button>
          )}
          <ul>
            {languages.map((lang) => (
              <li key={`header-lang-${lang}`}>
                <button
                  type="button"
                  onClick={() => {
                    changeLanguage(lang);
                    toggleLanguageMenu(false);
                  }}
                  className={cn('Header-Language-item', { active: lang === currentLanguage })}
                >
                  {lang?.split('-')[0].toUpperCase()}
                </button>
              </li>
            ))}
          </ul>
          {siteInfoData.searchPageUrl ? <SearchPageField searchUrl={siteInfoData.searchPageUrl} /> : null}
        </nav>

        <RouterLink to={siteInfoData.rootUrl} className="Header-Logo">
          <GutmannLogo />
        </RouterLink>

        <button type="button" className="Header-Navigation-toggle" onClick={() => toggleMenu()}>
          ≡
        </button>
        <nav className={cn('Header-Navigation', { 'is-visible': menuIsVisible })}>
          <button
            type="button"
            className="Header-Navigation-close"
            onClick={() => {
              toggleMenu(false);
            }}
          >
            &times;
          </button>
          <ul>
            {needsNavigation &&
              siteInfoData.navigation.map((navItem, index) => (
                // biome-ignore lint/suspicious: Static navigation
                <li key={index}>
                  <SimpleLink simpleLink={navItem} onClick={() => menuIsVisible && toggleMenu(false)} />
                </li>
              ))}
          </ul>
        </nav>
        <div className="Header-heading">
          <h1>{pageHeaderData.pageImageOverlayText}</h1>
        </div>
        {pageHeaderData.authLink ? <AuthButton authLink={pageHeaderData.authLink} /> : null}
      </ParallaxHeader>
    </div>
  );
}

export { PageHeader };
